import { useCallback, useEffect, useMemo, useRef } from 'react'
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import GroupsOutlined from '@mui/icons-material/GroupsOutlined'
import PersonOutline from '@mui/icons-material/PersonOutline'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'

import { useCampaigns } from '@/features/campaigns/api/campaign-queries'
import { useCampaignListInstrumentMetrics } from '@/features/campaigns/api/useCampaignListInstrumentMetrics'
import { enrichCampaignsWithInstrumentMetrics, sumTotalRewardsDistributedUsd } from '@/features/campaigns/lib/campaign-list-metrics'
import { countActiveLandingCampaigns } from '@/features/campaigns/lib/campaign-landing-display'
import { useToast } from '@/shared/toast/ToastProvider'

import { useAdminDashboardMetrics } from '../api/admin-dashboard-queries'
import { ADMIN_DASHBOARD_LAYOUT } from '../lib/admin-dashboard-layout'
import { AdminDashboardSkeleton } from './AdminDashboardSkeleton'
import { DashboardMetricCard } from './DashboardMetricCard'
import { RecentCampaignsSection } from './RecentCampaignsSection'
import { DashboardDirectInstrumentsSection } from './DashboardDirectInstrumentsSection'

const REFETCH_INTERVAL_MS = 30_000
const LOAD_ERROR_MESSAGE = 'Unable to load dashboard data. Please refresh.'

export function AdminDashboard() {
  const { showToast } = useToast()
  const errorToastShown = useRef(false)

  const metricsQuery = useAdminDashboardMetrics()
  const campaignsQuery = useCampaigns({ refetchIntervalMs: REFETCH_INTERVAL_MS })
  const campaigns = campaignsQuery.data?.items ?? []
  const campaignIds = useMemo(() => campaigns.map((c) => c.id), [campaigns])
  const { metricsByCampaignId, isLoading: instrumentMetricsLoading } =
    useCampaignListInstrumentMetrics(campaignIds)

  const enrichedCampaigns = useMemo(
    () => enrichCampaignsWithInstrumentMetrics(campaigns, metricsByCampaignId),
    [campaigns, metricsByCampaignId],
  )

  const activeCampaignCount = useMemo(
    () => countActiveLandingCampaigns(enrichedCampaigns),
    [enrichedCampaigns],
  )

  const totalIncentivesDistributedUsd = useMemo(() => {
    const fromInstruments = sumTotalRewardsDistributedUsd(metricsByCampaignId)
    const fromApi = metricsQuery.data?.totalIncentivesDistributedUsd ?? 0
    return fromInstruments > 0 ? fromInstruments : fromApi
  }, [metricsByCampaignId, metricsQuery.data?.totalIncentivesDistributedUsd])

  const isLoading =
    metricsQuery.isLoading || campaignsQuery.isLoading || instrumentMetricsLoading
  const isError = metricsQuery.isError || campaignsQuery.isError

  const handleRetry = useCallback(() => {
    void Promise.all([metricsQuery.refetch(), campaignsQuery.refetch()])
  }, [metricsQuery, campaignsQuery])

  useEffect(() => {
    if (isError && !errorToastShown.current) {
      errorToastShown.current = true
      showToast(LOAD_ERROR_MESSAGE, 'error', undefined, {
        actionLabel: 'Retry',
        onAction: handleRetry,
      })
    }
    if (!isError) {
      errorToastShown.current = false
    }
  }, [isError, showToast, handleRetry])

  const metrics = metricsQuery.data

  if (isLoading) {
    return <AdminDashboardSkeleton />
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 8,
        }}
        data-testid="admin-dashboard-load-error"
      >
        <Button
          variant="contained"
          onClick={handleRetry}
          data-testid="admin-dashboard-retry"
          sx={{ textTransform: 'none', borderRadius: '8px' }}
        >
          Retry
        </Button>
      </Box>
    )
  }

  const tileValues = [
    activeCampaignCount,
    metrics?.verifiedSignalProviders ?? 0,
    totalIncentivesDistributedUsd,
    metrics?.sensemakerEditorViewerCount ?? 0,
  ]

  const metricTiles = [
    {
      'data-testid': 'sensemaker-ftux-metric-campaigns',
      cardBg: '#daf5e4',
      iconBg: '#3f8367',
      Icon: FolderOutlined,
      value: tileValues[0],
      valueFormat: 'count' as const,
      label: 'Total Active Campaigns',
      labelColor: '#3f8367',
    },
    {
      'data-testid': 'sensemaker-ftux-metric-providers',
      cardBg: '#fae6dc',
      iconBg: '#df6534',
      Icon: PersonOutline,
      value: tileValues[1],
      valueFormat: 'count' as const,
      label: 'Total Signal Providers',
      labelColor: '#df6534',
    },
    {
      'data-testid': 'sensemaker-ftux-metric-incentives',
      cardBg: '#fae3ed',
      iconBg: '#ff5959',
      Icon: AccountBalanceWalletOutlined,
      value: tileValues[2],
      valueFormat: 'currency' as const,
      label: 'Total Incentives Distributed',
      labelColor: '#ff5959',
    },
    {
      'data-testid': 'sensemaker-ftux-metric-team',
      cardBg: '#c7ebf9',
      iconBg: '#0c9eeb',
      Icon: GroupsOutlined,
      value: tileValues[3],
      valueFormat: 'count' as const,
      label: 'Total Sensemaker Editors/Viewers',
      labelColor: '#0c9eeb',
    },
  ] as const

  return (
    <>
      <Grid
        container
        spacing={ADMIN_DASHBOARD_LAYOUT.metricGridGap}
        sx={{ mb: ADMIN_DASHBOARD_LAYOUT.metricsToRecentGap }}
      >
        {metricTiles.map((m) => (
          <Grid key={m.label} item xs={12} sm={6} lg={3}>
            <DashboardMetricCard {...m} />
          </Grid>
        ))}
      </Grid>

      <RecentCampaignsSection campaigns={campaigns} />
      <DashboardDirectInstrumentsSection canMutate />
    </>
  )
}
